<template>
    <div class="table-filters-container" id="table-filters-container">
        <TableFilterChips
            v-if="tableOpen"
            :filter-state="filterState"
            @remove-filter="removeFilter"
        />
        <Transition :name="filterTransitionName">
            <div
                v-if="showFilters && tableOpen"
                class="table-filters pa-4"
                :data-cy="`table.filters`"
            >
                <div class="full-row">
                    <p
                        class="bold grey01--text mb-2"
                        :data-cy="`table.filters.title`"
                    >
                        FILTERS
                    </p>
                    <p
                        class="grey01--text mb-2"
                        :data-cy="`table.filters.description`"
                    >
                        Enable Boolean text filtering with capitalized AND, OR,
                        NOT, AND NOT, OR NOT. Grouping with parentheses allowed.
                    </p>
                    <p
                        class="bold grey01--text small mb-3"
                        :data-cy="`table.filters.sub-text`"
                    >
                        Example: "vehicle AND car," "NOT car," "(vehicle AND
                        drive) AND NOT car."
                    </p>
                </div>
                <form @submit.prevent="applyFilters()">
                    <div class="flex-row">
                        <div
                            v-for="(filter, index) in localAppliedFilters"
                            :key="index + '_filter'"
                            class="filter half-row my-2"
                            :data-cy="`table.filters.${index}`"
                        >
                            <font-awesome-icon
                                icon="fas fa-trash-can"
                                class="icon ml-1 mr-2 mb-3 grey03--text"
                                :data-cy="`table.filters.${index}.trash`"
                                @click="removeFilterByIndex(index)"
                            ></font-awesome-icon>
                            <AonDropdown
                                :key="keyUpdater"
                                class="mr-2"
                                :items="initFilterOptions"
                                :init-selection="filter.column"
                                width="25%"
                                placeholder-trigger-color
                                :data-cy="`table.filters.${index}.select-column`"
                                @select-cleared="filterValue = null"
                                @select-option="selectFilterType($event, index)"
                            />
                            <div class="filter-type-container">
                                <StringFilter
                                    v-if="filter.column.filterType === 'string'"
                                    :ref="index + '_filter'"
                                    v-tooltip="{
                                        content: filter.column.toolTip,
                                    }"
                                    :default-filter="
                                        filter.column.defaultFilter || null
                                    "
                                    :column-name="filter.column.value"
                                    :column-label="filter.column.locale"
                                    :filter-state="filter.state"
                                    :filters="filter.column.filters"
                                    :data-cy="`table.filters.${index}.string-filter`"
                                    @onFilter="onFilter($event, index)"
                                />
                                <DateFilter
                                    v-else-if="
                                        filter.column.filterType === 'date' || filter.column.filterType === 'date-time'
                                    "
                                    :ref="index + '_filter'"
                                    v-tooltip="{
                                        content: filter.column.toolTip,
                                    }"
                                    :column-name="filter.column.value"
                                    :column-label="filter.column.locale"
                                    :filter-state="filter.state"
                                    :last-column="columns.length - 1 === index"
                                    :data-cy="`table.filters.${index}.date-filter`"
                                    :date-filter-type="`${filter.column.filterType} range`"
                                    @onFilter="onFilter($event, index)"
                                />
                                <NumberFilter
                                    v-else-if="
                                        filter.column.filterType === 'number'
                                    "
                                    :ref="index + '_filter'"
                                    v-tooltip="{
                                        content: filter.column.toolTip,
                                    }"
                                    :default-filter="
                                        filter.column.defaultFilter || null
                                    "
                                    :column-name="filter.column.value"
                                    :column-label="filter.column.locale"
                                    :filter-state="filter.state"
                                    :filters="filter.column.filters"
                                    :data-cy="`table.filters.${index}.number-filter`"
                                    @onFilter="onFilter($event, index)"
                                    @input-error="inputError"
                                />
                                <DropDownFilter
                                    v-else-if="
                                        filter.column.filterType ===
                                            'dropdown' ||
                                        filter.column.filterType ===
                                            'checked-dropdown'
                                    "
                                    :ref="index + '_filter'"
                                    :key="index+filter.column.value"
                                    v-tooltip="{
                                        content: filter.column.toolTip,
                                    }"
                                    :column-name="filter.column.value"
                                    :column-label="filter.column.locale"
                                    :filter-state="filter.state"
                                    :options="filter.column.filterItems"
                                    :data-cy="`table.filters.${index}.dropdown-filter`"
                                    :checkbox="
                                        filter.column.filterType ===
                                        'checked-dropdown'
                                    "
                                    :filter-variant="filter.column.filterVariant ?? 'word match'"
                                    :use-item-text-for-display="filter.column.useItemTextForDisplay ?? false"
                                    @onFilter="onFilter($event, index)"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="full-row mt-3">
                        <AonButton
                            id="add-filter"
                            text-with-icon
                            type="subtle"
                            label="Add Another Filter"
                            :icon-options="{
                                iconStyle: 'fas',
                                iconName: 'fa-circle-plus',
                            }"
                            :data-cy="`table.filters.add-another-filter`"
                            @clicked="addFilter"
                        />
                        <AonButton
                            class="ml-3"
                            label="Apply Filters"
                            is-form-submit
                            :disabled="!hasActiveFilters || disableButton"
                            :data-cy="`table.filters.apply-filters`"
                            @clicked="applyFilters()"
                        />
                    </div>
                </form>
            </div>
        </Transition>
    </div>
</template>

<script setup>
import { ref, onMounted, computed, nextTick, watch } from 'vue';
import AonButton from '@moatmetrics/armory/src/components/base/AonButton.vue';
import AonDropdown from '@moatmetrics/armory/src/components/base/forms/AonDropdown.vue';

import DateFilter from './filters/DateFilter.vue';
import DropDownFilter from './filters/DropDownFilter.vue';
import NumberFilter from './filters/NumberFilter.vue';
import StringFilter from './filters/StringFilter.vue';
import TableFilterChips from './TableFilterChips.vue';

const emit = defineEmits(['apply-table-filters', 'add-filter', 'remove-filter']);
const props = defineProps({
    columns: {
        type: Array,
        default: () => [],
    },
    filterState: {
        type: Array,
        default: () => [],
    },
    showFilters: {
        type: Boolean,
        default: true,
    },
    tableOpen: {
        type: Boolean,
        default: true,
    },
    filterTransitionName: {
        type: String,
        default: 'slide-down',
    },
    responsiveFilterDropdowns: {
        type: Boolean,
        default: false,
    },
});

/**
 * column: { column data },
 * state: { filter state data }
 */
const localAppliedFilters = ref([]);
const allPotentialFilters = ref([]);
const disableButton = ref(false);
const initFilterOptions = ref([]);
const keyUpdater = ref(0);

const hasActiveFilters = computed(() => {
    if (!localAppliedFilters.value?.length > 0) { return true };

    const anyInactiveFilters = localAppliedFilters.value.some((filter) =>
        !filter.state || (!filter.state.filterValue && filter.state.filterType !== 'Empty')
    );

    if (anyInactiveFilters) { return false };

    return true;
});
const initialFilterSelected = computed(() => {
    const index = initFilterOptions.value.findIndex(
        (val) => val.defaultFilterColumn
    );

    return initFilterOptions.value[index === -1 ? 0 : index];
});

const initColumns = async () => {
    initFilterOptions.value = props.columns
        .filter((val) => !val.hideFromFilters)
        .map((item) => ({
            ...item,
            itemText: item.locale,
            itemValue: item.value,
            filterActive:
                item.filterActive === undefined ? true : item.filterActive,
        }));
    allPotentialFilters.value = [...initFilterOptions.value]; // Shallow clone

    // filterState not available until after next tick...
    await nextTick();

    if (props.filterState && props.filterState.length > 0) {
        props.filterState.forEach((filter, index) => {
            const column = initFilterOptions.value.find(
                (val) => val.value === filter.filterColumn
            );
            selectFilterType(column, index);
            localAppliedFilters.value[index].state = filter;
        });
    } else if(initFilterOptions.value?.length > 0 && localAppliedFilters.value.length === 0) {
        addFilter();
    }
};

onMounted(async () => {
    await initColumns();
});

watch(
    () => props.columns,
    async () => {
        await initColumns();
    }
);

const onFilter = (detail, index) => {
    localAppliedFilters.value[index].state = detail;
};

const removeFilter = (filter) => {
    const index = localAppliedFilters.value?.findIndex((f) =>
        filter.filterColumn === f.state.filterColumn &&
        filter.filterType === f.state.filterType &&
        filter.filterValue === f.state.filterValue
    );

    // Already removed via the trash can icon
    if (index === -1) {
        applyFilters()
        return
    }
    removeFilterByIndex(index, true);
};

const addFilter = () => {
    const newFilter = initialFilterSelected.value;

    localAppliedFilters.value = localAppliedFilters.value.filter(
        (x) => !!x.column
    );
    localAppliedFilters.value.push({ column: newFilter });
    if (props.responsiveFilterDropdowns) {
        initFilterOptions.value.shift();
    }

    emit('add-filter', localAppliedFilters.value.length)
};

const removeFilterByIndex = (index, apply = false) => {
    if (props.responsiveFilterDropdowns) {
        let targetFilterToAddBack = allPotentialFilters.value.find(
            (filter) =>
                filter.value === localAppliedFilters.value[index].column.value
        );
        initFilterOptions.value.push(targetFilterToAddBack);
    }
    localAppliedFilters.value.splice(index, 1);
    keyUpdater.value++;

    if (apply) {
        applyFilters();
    }

    emit('remove-filter', localAppliedFilters.value.length)
};

const selectFilterType = (event, index) => {
    // TODO this should trigger a filter update if already applied
    localAppliedFilters.value[index] = { column: event };

    if (props.responsiveFilterDropdowns) {
        initFilterOptions.value = initFilterOptions.value.filter(
            (item) => item.itemValue !== event.itemValue
        );
    }
};

const applyFilters = () => {
    if (!hasActiveFilters.value || disableButton.value) { return; }

    const state = localAppliedFilters.value.map((val) => val.state);

    emit('apply-table-filters', state);
};

const inputError = (val) => {
    disableButton.value = val;
};
</script>

<style lang="scss" scoped>
.table-filters {
    width: 100%;
    border-top: solid 1px $grey05;
    background: $grey06;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    position: relative;
    height: 100%;

    #add-filter {
        min-width: 200px !important;
    }

    .icon {
        cursor: pointer;

        &.icon-close {
            width: 24px;
            height: 24px;
        }
    }

    .full-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        align-items: stretch;
        width: 100%;
        display: inline;
    }

    .flex-row {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        align-items: stretch;
        width: 100%;
    }

    .half-row {
        display: flex;
        align-items: center;
        align-items: stretch;
        width: 50%;
        display: inline;
    }

    .button-container {
        width: 100%;
    }
    .filter {
        display: flex;
        align-items: end;
    }
    .filter-type-container {
        width: 70%;
    }
}
.slide-down-enter-active {
    transition: all 0.3s ease-out;
}

.slide-down-leave-active {
    transition: all 0.2s ease-in;
}

.slide-down-enter-from,
.slide-down-leave-to {
    transform: translateY(-20px);
    opacity: 0;
}
</style>
